import { useQuery } from "@tanstack/react-query";
import { format, isToday } from "date-fns";
import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { Button, Icon, Loader } from "semantic-ui-react";
import { colors, style } from "../../style";
import { Card } from "../ui";

const { amber, amberTint, amberBorder, greenTint, greenText } = colors;

const MAX_RETRIES = 3;
// Waits between automatic retries once the error is showing; none after the last.
const AUTO_RETRY_SECONDS = [30, 60, 120];
const SLOW_AFTER_MS = 6000;

export const isIpecsUnavailable = (error) =>
  error?.response?.data?.code === "ipecs_unavailable";

// Timeouts and rate limits clear by themselves, unlike other 4xx.
const TRANSIENT_STATUSES = [408, 429, 502, 503, 504];

// Won't change on retry, and may mean access was revoked, so rows are never
// kept on screen for it.
const isClientError = (error) => {
  const status = error?.response?.status;
  return status >= 400 && status < 500 && !TRANSIENT_STATUSES.includes(status);
};

// Errors likely to clear by themselves. Others (e.g. a 500 for a user with
// no extension) only get the manual Try again.
const isTransientError = (error) => {
  const status = error?.response?.status;
  return (
    !status || TRANSIENT_STATUSES.includes(status) || isIpecsUnavailable(error)
  );
};

const formatTime = (timestamp) =>
  format(timestamp, isToday(timestamp) ? "HH:mm" : "EEE d MMM, HH:mm");

/**
 * useQuery for the call recordings list plus what to show for it: a loading
 * state, an error card, or rows with an optional notice ("slow", "cached" =
 * this search's stale rows, "previous" = the last search that loaded).
 *
 * Each attempt can mean another iPECS login, so automatic retries after an
 * error make a single attempt rather than running the query's own retries.
 */
export function useRecordingsQuery(queryKey, queryFn, options) {
  const autoRetrying = useRef(false);
  const query = useQuery(queryKey, queryFn, {
    ...options,
    retry: (failureCount, error) =>
      !autoRetrying.current &&
      isTransientError(error) &&
      failureCount < MAX_RETRIES,
    retryDelay: (attempt) => 2000 * 2 ** attempt,
    // Polling for transcriptions would only add iPECS logins during an error.
    refetchInterval: (data, q) => {
      if (q.state.status === "error") return false;
      const interval = options.refetchInterval;
      return typeof interval === "function"
        ? interval(data, q)
        : (interval ?? false);
    },
  });
  const { data, error, isError, isSuccess, isFetching, isPreviousData } = query;
  const keyHash = JSON.stringify(queryKey);
  const ownData = isPreviousData ? undefined : data;

  const lastGood = useRef(null);
  const failedKeys = useRef(new Set());
  const lastFailure = useRef(null);
  if (isSuccess && ownData) {
    lastGood.current = { data: ownData, queryKey, keyHash };
    failedKeys.current.delete(keyHash);
    lastFailure.current = null;
  }
  const failure = isError ? error : query.failureReason;
  if (failure) lastFailure.current = { error: failure, keyHash };
  if (isError && !isClientError(error)) failedKeys.current.add(keyHash);

  const fallback =
    lastGood.current && lastGood.current.keyHash !== keyHash
      ? lastGood.current
      : null;

  // Slow once a fetch with nothing of its own to show has run for a while.
  const [slowTimer, setSlowTimer] = useState(false);
  const waiting = isFetching && !ownData;
  useEffect(() => {
    if (!waiting) {
      setSlowTimer(false);
      return;
    }
    const timer = setTimeout(() => setSlowTimer(true), SLOW_AFTER_MS);
    return () => clearTimeout(timer);
  }, [waiting]);
  const slow = slowTimer || query.failureCount > 0;

  let mode = "rows";
  let rows = ownData;
  let notice = null;
  if (isError) {
    if (isClientError(error) || (!ownData && !fallback)) {
      mode = "error";
    } else {
      rows = ownData ?? fallback.data;
      notice = ownData ? "cached" : "previous";
    }
  } else if (!ownData) {
    if (failedKeys.current.has(keyHash) && fallback) {
      // Retrying a search that failed: keep the notice up until it resolves.
      rows = fallback.data;
      notice = "previous";
    } else if (fallback) {
      rows = fallback.data;
      notice = slow ? "slow" : null;
    } else {
      mode = "loading";
    }
  }

  const lastError =
    lastFailure.current?.keyHash === keyHash ? lastFailure.current.error : null;
  // null until something fails: slowness alone isn't blamed on iPECS.
  const ipecsIssue = lastError ? isIpecsUnavailable(lastError) : null;
  let actionsDisabledReason = null;
  if (mode === "rows" && (notice === "cached" || rows !== ownData)) {
    actionsDisabledReason = isIpecsUnavailable(lastError)
      ? "Needs iPECS: available once it's back"
      : "Available once your call recordings load";
  }

  // Toast only for a real fetch that succeeded after an iPECS error was shown.
  const ipecsErrorAt = useRef(0);
  if (isError && isIpecsUnavailable(error)) {
    ipecsErrorAt.current = query.errorUpdatedAt;
  }
  const { dataUpdatedAt } = query;
  useEffect(() => {
    if (
      isSuccess &&
      !isPreviousData &&
      ipecsErrorAt.current &&
      dataUpdatedAt > ipecsErrorAt.current
    ) {
      ipecsErrorAt.current = 0;
      toast.success("iPECS is back. Your call recordings are up to date.", {
        toastId: "ipecs-recovered",
        autoClose: 4000,
      });
    }
  }, [isSuccess, isPreviousData, dataUpdatedAt]);

  // Each search or page gets its own auto retries. Declared before the
  // countdown effect so the reset lands first.
  const autoRetries = useRef(0);
  useEffect(() => {
    autoRetries.current = 0;
  }, [keyHash, dataUpdatedAt]);

  const [retryIn, setRetryIn] = useState(null);
  const transient = isError && isTransientError(error);
  const { errorUpdatedAt, refetch } = query;
  useEffect(() => {
    const wait = AUTO_RETRY_SECONDS[autoRetries.current];
    if (!transient || isFetching || wait === undefined) {
      setRetryIn(null);
      return;
    }
    let remaining = wait;
    setRetryIn(remaining);
    const timer = setInterval(() => {
      remaining -= 1;
      if (remaining > 0) {
        setRetryIn(remaining);
        return;
      }
      clearInterval(timer);
      autoRetries.current += 1;
      autoRetrying.current = true;
      refetch().finally(() => {
        autoRetrying.current = false;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [transient, isFetching, errorUpdatedAt, keyHash, refetch]);

  return {
    query,
    mode,
    rows,
    notice,
    fallbackKey: notice === "previous" ? fallback?.queryKey : null,
    updatedAt: notice === "cached" ? formatTime(dataUpdatedAt) : null,
    actionsDisabledReason,
    ipecsIssue,
    slow,
    attempt: Math.min(query.failureCount + 1, MAX_RETRIES + 1),
    // An auto retry is a single attempt.
    maxAttempts: autoRetrying.current ? 1 : MAX_RETRIES + 1,
    retryIn,
    retrying: isFetching,
    retry: () => refetch(),
  };
}

const formatCountdown = (seconds) =>
  `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;

function AttemptPips({ attempt, maxAttempts }) {
  return (
    <div style={{ display: "flex", gap: "4px" }} aria-hidden="true">
      {Array.from({ length: maxAttempts }, (_, i) => (
        <span
          key={i}
          style={{
            width: "18px",
            height: "4px",
            borderRadius: "2px",
            background: i < attempt ? amber : "rgba(255, 255, 255, 0.15)",
          }}
        />
      ))}
    </div>
  );
}

function SkeletonRows({ columns, rows = 6 }) {
  return (
    <div style={{ overflowX: "auto" }} aria-hidden="true">
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <tbody>
          {Array.from({ length: rows }, (_, row) => (
            <tr key={row} style={{ opacity: 1 - row * 0.12 }}>
              {Array.from({ length: columns }, (_, col) => (
                <td
                  key={col}
                  style={{
                    padding: "16px 10px",
                    borderBottom: `1px solid ${colors.border}`,
                  }}
                >
                  <div
                    className="recordings-skeleton"
                    style={
                      col === columns - 1
                        ? {
                            width: "118px",
                            height: "32px",
                            borderRadius: "8px",
                          }
                        : { width: `${60 + ((row * 7 + col * 13) % 5) * 15}px` }
                    }
                  />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function RecordingsLoading({
  slow,
  ipecsIssue,
  attempt,
  maxAttempts,
  columns,
}) {
  let title = "Fetching your call recordings from iPECS";
  let body =
    "This can take a few seconds while we sign in to iPECS call recording.";
  if (slow && ipecsIssue) {
    title = "iPECS is taking longer than usual to respond";
    body =
      "The iPECS call recording service is slow to let us sign in right now. We're still trying, so there's no need to refresh.";
  } else if (slow) {
    title = "Your call recordings are taking longer than usual to load";
    body = "We're still trying, so there's no need to refresh.";
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "15px" }}>
      <div
        role="status"
        style={{
          background: colors.surface,
          border: `1px solid ${slow ? amberBorder : colors.border}`,
          borderRadius: "10px",
          overflow: "hidden",
        }}
      >
        <div className="recordings-progress">
          <div
            style={{
              background: slow ? amber : colors.teal,
            }}
          />
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "16px",
            flexWrap: "wrap",
            padding: "16px 20px",
          }}
        >
          {slow ? (
            <Icon
              name="clock outline"
              size="large"
              style={{ color: amber, margin: 0 }}
            />
          ) : (
            <Loader active inline inverted size="small" />
          )}
          <div style={{ flex: "1 1 280px" }}>
            <div
              style={{ fontWeight: 700, fontSize: "15px", color: colors.text }}
            >
              {title}
            </div>
            <div style={{ color: colors.textMuted, marginTop: "2px" }}>
              {body}
            </div>
          </div>
          {slow && maxAttempts > 1 && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-end",
                gap: "6px",
              }}
            >
              <AttemptPips attempt={attempt} maxAttempts={maxAttempts} />
              <div style={{ fontSize: "12px", color: colors.textMuted }}>
                Attempt {attempt} of {maxAttempts}
              </div>
            </div>
          )}
        </div>
      </div>
      <SkeletonRows columns={columns} />
    </div>
  );
}

export function RecordingsError({
  error,
  retrying,
  retryIn,
  onRetry,
  onViewProcessed,
}) {
  const ipecs = isIpecsUnavailable(error);
  const forbidden = error?.response?.status === 403;

  let title = "We couldn't load your call recordings";
  let body = "Please try again in a moment.";
  if (ipecs) {
    title = "iPECS call recording isn't responding";
    body =
      "We tried a few times but iPECS didn't accept our sign-in, so we can't fetch your recordings just now. This is usually temporary and clears up within a few minutes.";
  } else if (forbidden) {
    title = "You can't view these call recordings";
    body =
      error.response.data?.message ??
      "You don't have access to this extension.";
  }

  return (
    <Card
      as="section"
      style={{
        margin: "24px auto 0",
        maxWidth: "580px",
        padding: "36px 32px 28px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "18px",
        textAlign: "center",
      }}
    >
      {ipecs && <ConnectionGraphic />}
      <div>
        <h2
          style={{
            margin: 0,
            fontSize: "20px",
            fontWeight: 700,
            color: colors.text,
          }}
        >
          {title}
        </h2>
        <p
          style={{
            margin: "8px 0 0",
            color: colors.textMuted,
            lineHeight: 1.55,
          }}
        >
          {body}
        </p>
      </div>
      {ipecs && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "6px",
            padding: "8px 14px",
            borderRadius: "999px",
            background: greenTint,
            color: greenText,
            fontSize: "13px",
            fontWeight: 700,
          }}
        >
          <Icon name="shield alternate" style={{ margin: 0 }} />
          Your recordings are safe on iPECS. Nothing has been lost.
        </div>
      )}
      <div
        style={{
          display: "flex",
          gap: "10px",
          flexWrap: "wrap",
          justifyContent: "center",
        }}
      >
        {!forbidden && (
          <Button
            style={style.primaryButton}
            loading={retrying}
            disabled={retrying}
            onClick={onRetry}
          >
            <Icon name="redo" />
            Try again
          </Button>
        )}
        {onViewProcessed && (
          <Button style={style.secondaryButton} onClick={onViewProcessed}>
            View processed calls
          </Button>
        )}
      </div>
      {retryIn && !retrying && (
        <div style={{ fontSize: "13px", color: colors.textMuted }}>
          We'll try again automatically in{" "}
          <strong
            style={{
              color: colors.textStrong,
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {formatCountdown(retryIn)}
          </strong>
        </div>
      )}
    </Card>
  );
}

function ConnectionGraphic() {
  const node = {
    width: "52px",
    height: "52px",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 900,
    fontSize: "12px",
  };
  return (
    <div style={{ display: "flex", alignItems: "center" }} aria-hidden="true">
      <div
        style={{
          ...node,
          background: colors.teal,
          color: colors.onPrimary,
        }}
      >
        SK
      </div>
      <div
        style={{
          width: "44px",
          borderTop: "2px solid rgba(255, 255, 255, 0.35)",
        }}
      />
      <div
        style={{
          width: "26px",
          height: "26px",
          borderRadius: "50%",
          background: amberTint,
          border: `1px solid ${amberBorder}`,
          color: amber,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Icon name="pause" size="small" style={{ margin: 0 }} />
      </div>
      <div
        style={{
          width: "44px",
          borderTop: "2px dashed rgba(255, 255, 255, 0.2)",
        }}
      />
      <div
        style={{
          ...node,
          border: "2px solid rgba(255, 255, 255, 0.25)",
          color: colors.textStrong,
        }}
      >
        iPECS
      </div>
    </div>
  );
}

/**
 * Sits above rows we're still showing while iPECS is struggling: the cached
 * list for this search, or the previous search's results.
 */
export function RecordingsNotice({
  kind,
  ipecsIssue,
  updatedAt,
  attempt,
  maxAttempts,
  retrying,
  retryIn,
  onRetry,
}) {
  let title;
  let body;
  if (kind === "slow") {
    title = ipecsIssue
      ? "iPECS is taking longer than usual to respond"
      : "This search is taking longer than usual";
    body =
      maxAttempts > 1
        ? `Still trying (attempt ${attempt} of ${maxAttempts}). Showing your previous results for now.`
        : "Still trying. Showing your previous results for now.";
  } else if (kind === "previous") {
    title = ipecsIssue
      ? "iPECS didn't respond to that search"
      : "That search didn't load";
    body = "Showing your previous results. Try the search again in a moment.";
  } else if (ipecsIssue) {
    title = `iPECS isn't responding right now, so this list is from ${updatedAt}`;
    body =
      "Calls made since then will show up once iPECS is back. Processing new calls is paused until then.";
  } else {
    title = `We couldn't refresh your call recordings, so this list is from ${updatedAt}`;
    body = "Try again in a moment.";
  }

  return (
    <div
      role="status"
      style={{
        display: "flex",
        alignItems: "center",
        gap: "14px",
        flexWrap: "wrap",
        padding: "14px 18px",
        marginBottom: "15px",
        borderRadius: "10px",
        background: amberTint,
        border: `1px solid ${amberBorder}`,
        color: colors.text,
      }}
    >
      {kind === "slow" ? (
        <Loader active inline inverted size="small" />
      ) : (
        <Icon
          name="exclamation circle"
          size="large"
          style={{ color: amber, margin: 0 }}
        />
      )}
      <div style={{ flex: "1 1 360px" }}>
        <div style={{ fontWeight: 700 }}>{title}</div>
        <div
          style={{
            color: colors.textMuted,
            fontSize: "13px",
            marginTop: "2px",
          }}
        >
          {body}
        </div>
      </div>
      {kind !== "slow" && (
        <>
          {retryIn && !retrying && (
            <span style={{ fontSize: "13px", color: colors.textMuted }}>
              Retrying in{" "}
              <strong
                style={{
                  color: colors.textStrong,
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {formatCountdown(retryIn)}
              </strong>
            </span>
          )}
          <Button
            size="small"
            loading={retrying}
            disabled={retrying}
            onClick={onRetry}
            style={{
              background: amber,
              color: colors.pageBackground,
              margin: 0,
            }}
          >
            <Icon name="redo" />
            Try again
          </Button>
        </>
      )}
    </div>
  );
}
